// Helpers for the ITBMS working paper. Amounts arrive from the API as strings ("1234.50").

export const toNumber = (value) => {
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

export const formatMoney = (value) => toNumber(value).toLocaleString('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

// The DGI form leaves zero boxes empty; a few totals always show their value.
const ALWAYS_SHOWN = new Set(['211', '49', '55', '59']);

export function casillaText(form, key) {
  const value = toNumber(form?.[key]);
  if (value === 0 && !ALWAYS_SHOWN.has(key)) return '';
  return formatMoney(value);
}

export const sumBy = (rows, field) => rows.reduce((total, row) => total + toNumber(row[field]), 0);

// Spreadsheet formulas can run when a CSV is opened in Excel: neutralise leading = + - @ in text.
export function csvCell(value) {
  let text = value === null || value === undefined ? '' : String(value);
  if (/^[=+\-@\t\r]/.test(text) && Number.isNaN(Number(text))) text = `'${text}`;
  return /[",\n\r;]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export const buildCsv = (header, rows) =>
  [header, ...rows].map((row) => row.map(csvCell).join(',')).join('\r\n');

export function salesCsv(sales) {
  return buildCsv(
    ['Factura', 'Fecha', 'Estado', 'Cliente', 'RUC', 'CUFE', 'Base', 'Tasa %', 'ITBMS pleno', 'ITBMS cobrado', 'ITBMS retenido'],
    sales.map((row) => [row.invoice_number, row.issue_date, row.status, row.client_name, row.client_ruc, row.cufe,
      row.base, row.rate, row.tax_full, row.tax_collected, row.tax_retained]),
  );
}

export const PURCHASE_REASONS = {
  sin_itbms: 'Sin ITBMS',
  sin_factura_de_respaldo: 'Sin factura de respaldo',
  sin_datos_empresa: 'Factura sin nombre/RUC de la empresa',
  tasa_atipica: 'Tasa atípica',
  pago_a_personas: 'Pago a ayudante o dueño',
};

export const PAYEE_OPTIONS = [
  { id: 'proveedor', label: 'Proveedor' },
  { id: 'profesional', label: 'Ayudante (servicios profesionales)' },
  { id: 'dueno', label: 'Dueño' },
];
export const PAYEE_LABELS = Object.fromEntries(PAYEE_OPTIONS.map((option) => [option.id, option.label]));

export const BUYER_LABELS = { ok: 'Con datos de la empresa', missing: 'Sin nombre/RUC de la empresa', unknown: 'Sin verificar' };

// Company expenses that give no ITBMS credit (exempt purchases are not listed: they carry no ITBMS;
// payments to helpers and the owner are not purchases at all and have their own list).
export const noCreditPurchases = (purchases) => purchases.filter((row) => !row.included && row.reason && row.reason !== 'sin_itbms' && row.reason !== 'pago_a_personas');

export const peoplePurchases = (purchases) => purchases.filter((row) => row.reason === 'pago_a_personas');

// One row per supplier of the period, to classify who each one is.
export function suppliersOfPeriod(purchases) {
  const byId = new Map();
  purchases.forEach((row) => {
    const key = row.supplier_id || row.supplier_name;
    const entry = byId.get(key) || { supplier_id: row.supplier_id, supplier_name: row.supplier_name, payee_type: row.payee_type || 'proveedor', count: 0, total: 0 };
    entry.count += 1;
    entry.total += toNumber(row.total);
    byId.set(key, entry);
  });
  return [...byId.values()].sort((a, b) => a.supplier_name.localeCompare(b.supplier_name));
}

// Difference between the DGI report and the ERP, as a short readable status.
export function dgiDifferenceStatus(month) {
  if (!month.entered) return { tone: 'text-[#6B7280]', text: 'Sin registrar' };
  const diff = toNumber(month.difference);
  if (Math.abs(diff) < 0.01) return { tone: 'text-emerald-400', text: 'Coincide' };
  return diff > 0
    ? { tone: 'text-amber-400', text: `Faltan B/. ${formatMoney(diff)} en el ERP` }
    : { tone: 'text-red-400', text: `El ERP tiene B/. ${formatMoney(-diff)} de más` };
}

export const MONTH_NAMES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
export const monthLabel = (code) => `${MONTH_NAMES[Number(code.slice(4)) - 1]} ${code.slice(0, 4)}`;

export const PURCHASE_FILTERS = [
  { id: 'all', label: 'Todas', test: () => true },
  { id: 'credit', label: 'Con crédito', test: (row) => row.included },
  { id: 'excluded', label: 'Sin crédito', test: (row) => !row.included },
  { id: 'no_buyer', label: 'Sin nombre/RUC de la empresa', test: (row) => row.buyer_check === 'missing' },
  { id: 'unverified', label: 'Sin verificar', test: (row) => row.buyer_check === 'unknown' && toNumber(row.tax) > 0 },
  { id: 'people', label: 'Ayudantes y dueño', test: (row) => row.reason === 'pago_a_personas' },
  { id: 'no_supplier_ruc', label: 'Proveedor sin RUC', test: (row) => !row.supplier_has_ruc && toNumber(row.tax) > 0 },
];

export function purchasesCsv(purchases) {
  return buildCsv(
    ['Orden', 'Fecha', 'Proveedor', 'RUC proveedor', 'CUFE', 'Subtotal', 'ITBMS', 'Tasa %', 'Respaldo', 'Empresa en factura', 'Cuenta para crédito', 'Motivo', 'Tipo de beneficiario'],
    purchases.map((row) => [row.po_number, row.date, row.supplier_name, row.supplier_ruc, row.cufe, row.subtotal, row.tax,
      row.rate, row.has_support ? 'Sí' : 'No', BUYER_LABELS[row.buyer_check] || '', row.included ? 'Sí' : 'No', PURCHASE_REASONS[row.reason] || row.reason, PAYEE_LABELS[row.payee_type] || '']),
  );
}

// Same column order as DGI Anexo 95 (Detalle de crédito de retención del ITBMS).
export function annex95Csv(annex) {
  return buildCsv(
    ['Tipo persona', 'RUC', 'DV', 'Nombre o razón social', 'N° factura/documento', 'Monto gravado ITBMS', 'ITBMS causado', 'Objeto de la retención', 'ITBMS retenido'],
    annex.map((row) => [row.person_type, row.ruc, row.dv, row.name, row.document, row.taxed_amount, row.itbms_caused,
      row.retention_object, row.itbms_retained]),
  );
}

export function downloadCsv(filename, content) {
  const blob = new Blob([`﻿${content}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

export function dueLabel(report) {
  if (report.status === 'presented') return 'Presentado';
  if (!report.period_ended) return `En curso · vence el ${report.due_date}`;
  if (report.days_to_due < 0) return `Vencido hace ${Math.abs(report.days_to_due)} día(s)`;
  if (report.days_to_due === 0) return 'Vence hoy';
  return `Vence en ${report.days_to_due} día(s)`;
}

// Groups for the adjustment dialog. `field` is "amount" or "tax"; the backend adds them to the calculated value.
export const ADJUSTMENT_GROUPS = [
  {
    title: 'Ventas y débito fiscal',
    lines: [
      { label: 'Gravadas 7% — ventas y servicios (11)', fields: [['11', 'amount', 'Monto'], ['11', 'tax', 'Impuesto']] },
      { label: 'Gravadas 7% — transporte aéreo (12)', fields: [['12', 'amount', 'Monto'], ['12', 'tax', 'Impuesto']] },
      { label: 'Gravadas 10% — bebidas alcohólicas (13)', fields: [['13', 'amount', 'Monto'], ['13', 'tax', 'Impuesto']] },
      { label: 'Gravadas 10% — hospedaje (14)', fields: [['14', 'amount', 'Monto'], ['14', 'tax', 'Impuesto']] },
      { label: 'Gravadas 15% — cigarrillos (15)', fields: [['15', 'amount', 'Monto'], ['15', 'tax', 'Impuesto']] },
      { label: 'Exportación y reexportación (16)', fields: [['16', 'amount', 'Monto']] },
      { label: 'Otras operaciones exentas (17)', fields: [['17', 'amount', 'Monto']] },
      { label: 'Operaciones no gravadas (18)', fields: [['18', 'amount', 'Monto']] },
      { label: 'Devoluciones y descuentos en compras (20 / 201)', fields: [['20', 'amount', 'Monto'], ['201', 'amount', 'Impuesto']] },
    ],
  },
  {
    title: 'Compras e ITBMS cargado',
    lines: [
      ...[[22, '7%'], [23, '10%'], [24, '15%']].map(([n, r]) => ({
        label: `Directas a operaciones gravadas ${r} (${n})`,
        fields: [[String(n), 'amount', 'Locales'], [`${n}1`, 'amount', 'Importaciones'], [`${n}3`, 'tax', 'ITBMS']],
      })),
      ...[[26, '7%'], [27, '10%'], [28, '15%']].map(([n, r]) => ({
        label: `Imputables a operaciones mixtas ${r} (${n})`,
        fields: [[String(n), 'amount', 'Locales'], [`${n}1`, 'amount', 'Importaciones'], [`${n}3`, 'tax', 'ITBMS']],
      })),
      ...[[30, '7%'], [31, '10%'], [32, '15%']].map(([n, r]) => ({
        label: `Destinadas a exportación ${r} (${n})`,
        fields: [[String(n), 'amount', 'Locales'], [`${n}1`, 'amount', 'Importaciones'], [`${n}3`, 'tax', 'ITBMS']],
      })),
      ...[[34, '7%'], [35, '10%'], [36, '15%']].map(([n, r]) => ({
        label: `Destinadas a operaciones exentas ${r} (${n})`,
        fields: [[String(n), 'amount', 'Locales'], [`${n}1`, 'amount', 'Importaciones'], [`${n}3`, 'tax', 'ITBMS']],
      })),
      { label: 'Compras e importaciones exentas (39 / 391)', fields: [['39', 'amount', 'Locales'], ['391', 'amount', 'Importaciones']] },
      { label: 'Devoluciones y descuentos en ventas gravadas (402 / 403)', fields: [['402', 'amount', 'Monto'], ['403', 'amount', 'ITBMS']] },
    ],
  },
  {
    title: 'Liquidación',
    lines: [
      { label: 'Saldo a favor de la declaración anterior (51)', fields: [['51', 'amount', 'Monto']] },
      { label: 'Crédito de retención del ITBMS (52)', fields: [['52', 'amount', 'Monto']] },
      { label: 'Crédito fiscal por operaciones al exterior (53)', fields: [['53', 'amount', 'Monto']] },
      { label: 'Recargo (56)', fields: [['56', 'amount', 'Monto']] },
      { label: 'Multa (57)', fields: [['57', 'amount', 'Monto']] },
      { label: 'Interés (58)', fields: [['58', 'amount', 'Monto']] },
    ],
  },
];
